/**
 * ChatPanel — the composer's real AI Chat surface.
 *
 * Multi-thread per Piece: each (workspace, piece, user) can have many threads.
 * Active thread ID lives in the URL hash as `#chat=<threadId>` so reload
 * restores the same thread. Header dropdown switches/renames/archives threads
 * and creates new ones (auto-titled from the first user message on the server).
 *
 * Streaming, per-user, per-Piece conversation backed by the `composer-chat`
 * edge function and `composer_chat_messages` table.
 *
 * Vault prompt-push: subscribes to `composerBus` `chat:insert` events and
 * pre-fills the textarea (focused, never auto-sent).
 *
 * Send-to-canvas: assistant messages get a "Send to Draft Canvas" action
 * that appends the message text (or the user's text selection inside the
 * bubble, when present) into the active draft at the cursor via
 * `composerBus` `draft:insert`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { toast } from "sonner";
import { ArrowUp, Copy, ExternalLink, FileDown, GripVertical, Loader2, MessageSquare, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import { ChatMarkdown } from "./ChatMarkdown";
import { useCorpusChipResolver } from "./useCorpusChipResolver";

import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
  type ToolPart,
} from "@/components/ai-elements/tool";
import { PromptInputSubmit } from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { composerBus, type DraftStreamMode } from "@/lib/composerBus";
import { composerChatApi, type ComposerChatThread } from "@/lib/composerChatApi";
import { markdownToTiptapDoc } from "@/lib/markdownToTiptap";
import { ChatThreadSwitcher } from "./ChatThreadSwitcher";
import { CorpusPickerPopover } from "./CorpusPickerPopover";
import { ChatComposerEditor, type ChatComposerEditorHandle, type ChatSegment } from "./ChatComposerEditor";
import { SlashInlinePicker } from "./SlashInlinePicker";
import { UserMessageBody } from "./UserMessageBody";
import { getThreadScope, setThreadScope, type ThreadScope } from "@/lib/pieceCorpusScope";

type Props = {
  workspaceId: string;
  pieceId: string;
};

type LocalError = {
  kind: "credits" | "rate-limit" | "generic";
  message: string;
};

const STARTER_PROMPTS = [
  "Summarize my latest draft in 3 bullets.",
  "Suggest a stronger opening for this piece.",
  "Brainstorm three angles I haven't tried yet.",
];

const messageText = (m: UIMessage): string =>
  m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");

// Markdown → TipTap doc lives in src/lib/markdownToTiptap.ts so it can be
// shared with future surfaces (per-message push + write-to-canvas mode).

const classifyError = (err: unknown): LocalError => {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  const lower = raw.toLowerCase();
  if (lower.includes("402") || lower.includes("credit") || lower.includes("exhaust")) {
    return { kind: "credits", message: raw || "Out of AI credits." };
  }
  if (lower.includes("429") || lower.includes("rate")) {
    return { kind: "rate-limit", message: raw || "Rate limited, try again in a moment." };
  }
  return { kind: "generic", message: raw || "Something went wrong." };
};

const getSelectionWithin = (container: HTMLElement | null): string | null => {
  if (!container) return null;
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;
  const text = sel.toString().trim();
  return text.length > 0 ? text : null;
};

/** Read `#chat=<threadId>` from `window.location.hash`. */
const readChatHash = (): string | null => {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  return params.get("chat");
};

/** Update `#chat=<threadId>` without adding a history entry. */
const writeChatHash = (threadId: string | null) => {
  if (typeof window === "undefined") return;
  const hash = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  if (threadId) params.set("chat", threadId);
  else params.delete("chat");
  const next = params.toString();
  const url = `${window.location.pathname}${window.location.search}${next ? `#${next}` : ""}`;
  window.history.replaceState(null, "", url);
};

const ChatInsertBridge = ({
  editorRef,
  onAutoSend,
}: {
  editorRef: React.MutableRefObject<ChatComposerEditorHandle | null>;
  onAutoSend?: (text: string) => void;
}) => {
  useEffect(() => {
    return composerBus.subscribe("chat:insert", (payload) => {
      const editor = editorRef.current;
      if (!editor) return;
      const existing = editor.getPlainText().trim();
      if (existing.length > 0) {
        const ok = window.confirm(
          "Replace your in-progress message with this prompt?",
        );
        if (!ok) return;
      }
      editor.setPlainText(payload.text);
      window.setTimeout(() => {
        editor.focus();
        if (payload.autoSend && payload.text.trim().length > 0) {
          onAutoSend?.(payload.text);
        }
      }, 0);
    });
  }, [editorRef, onAutoSend]);
  return null;
};

// SlashInlinePicker moved to ./SlashInlinePicker.tsx so BriefIntakeChat and
// TemplateEditor can share the same slash-mention behavior.

const STARTER_PROMPT_LABELS = STARTER_PROMPTS;

const StarterChips = ({ onPick }: { onPick: (text: string) => void }) => (
  <div className="mt-3 flex flex-wrap justify-center gap-1.5">
    {STARTER_PROMPT_LABELS.map((p) => (
      <button
        key={p}
        type="button"
        onClick={() => onPick(p)}
        className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
      >
        {p}
      </button>
    ))}
  </div>
);


// ---------- Inner per-thread view ----------

type ThreadViewProps = {
  workspaceId: string;
  pieceId: string;
  threadId: string | null;
  onThreadCreated: (threadId: string) => void;
  onTurnFinished: () => void;
  readOnly?: boolean;
};

type ThreadRuntimeProps = ThreadViewProps & {
  initialMessages: UIMessage[];
  initialSegmentsById: Map<string, ChatSegment[]>;
};

const ChatThreadView = (props: ThreadViewProps) => {
  const { threadId } = props;
  const [loaded, setLoaded] = useState<
    | { messages: UIMessage[]; segments: Map<string, ChatSegment[]> }
    | null
  >(null);

  // Load persisted history before mounting the AI SDK chat runtime. `useChat`
  // only treats its `messages` prop as initial state, so mounting it with []
  // before the fetch resolves leaves existing threads blank after selection.
  useEffect(() => {
    let cancelled = false;
    const initialThreadId = threadId;
    if (!initialThreadId) {
      setLoaded({ messages: [], segments: new Map() });
      return;
    }
    void (async () => {
      const fetchRows = async () => {
        const { data: rows, error } = await supabase
          .from("composer_chat_messages")
          .select("id, ai_sdk_id, role, parts, segments, created_at")
          .eq("thread_id", initialThreadId)
          .order("created_at", { ascending: true });
        if (error) throw error;
        return rows ?? [];
      };
      try {
        let rows = await fetchRows();
        if (!cancelled && rows.length === 0) {
          await new Promise((r) => setTimeout(r, 800));
          if (cancelled) return;
          rows = await fetchRows();
        }
        if (cancelled) return;
        const segMap = new Map<string, ChatSegment[]>();
        const hist: UIMessage[] = rows.map((r, i) => {
          const msgId =
            (r as { ai_sdk_id: string | null }).ai_sdk_id ||
            `db_${(r as { id: string }).id}` ||
            `hist_${i}`;
          const rawSegs = (r as { segments?: unknown }).segments;
          if (Array.isArray(rawSegs) && rawSegs.length > 0) {
            segMap.set(msgId, rawSegs as ChatSegment[]);
          }
          return {
            id: msgId,
            role: (r as { role: string }).role as UIMessage["role"],
            parts: ((r as { parts: unknown }).parts ?? []) as UIMessage["parts"],
          };
        });
        setLoaded({ messages: hist, segments: segMap });
      } catch (err) {
        console.warn("ChatPanel: history load failed", err);
        if (!cancelled) setLoaded({ messages: [], segments: new Map() });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot per keyed mount; threadId adoption after stream is handled by the runtime ref.
  }, []);

  if (loaded === null) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <ChatThreadRuntime
      {...props}
      initialMessages={loaded.messages}
      initialSegmentsById={loaded.segments}
    />
  );
};

const ChatThreadRuntime = ({
  workspaceId,
  pieceId,
  threadId,
  onThreadCreated,
  onTurnFinished,
  initialMessages,
  initialSegmentsById,
  readOnly = false,
}: ThreadRuntimeProps) => {
  const composerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<ChatComposerEditorHandle | null>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const focusComposer = useCallback(() => {
    editorRef.current?.focus();
  }, []);
  const [localError, setLocalError] = useState<LocalError | null>(null);
  const lastUserTextRef = useRef<string | null>(null);
  const threadCreateNotifiedRef = useRef<string | null>(null);
  const [canvasMode, setCanvasMode] = useState<"off" | DraftStreamMode>("off");
  const writeToCanvas = canvasMode !== "off";
  const [scope, setScopeState] = useState<ThreadScope>({ documentIds: [], folderIds: [], buckets: [] });
  const [slashOpen, setSlashOpen] = useState(false);
  const resolveChip = useCorpusChipResolver(workspaceId);


  // Segments-by-message-id map so user bubbles can render chip pills in
  // rendered history. Pre-seeded from persisted rows loaded by ChatThreadView.
  const [segmentsById, setSegmentsById] = useState<Map<string, ChatSegment[]>>(
    () => new Map(initialSegmentsById ?? []),
  );
  const pendingSegmentsRef = useRef<ChatSegment[] | null>(null);

  // Resizable composer (drag handle above the input surface).
  const COMPOSER_MIN = 88;
  const COMPOSER_DEFAULT = 160;
  const composerStorageKey = `composer:chatInputHeight:${workspaceId}`;
  const [composerHeight, setComposerHeight] = useState<number>(COMPOSER_DEFAULT);
  const composerDragRef = useRef<{ startY: number; startH: number } | null>(null);
  const composerHeightRef = useRef<number>(COMPOSER_DEFAULT);
  const composerMaxRef = useRef<number>(520);
  useEffect(() => {
    composerHeightRef.current = composerHeight;
  }, [composerHeight]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(composerStorageKey);
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed) && parsed >= COMPOSER_MIN) setComposerHeight(parsed);
    } catch {
      /* noop */
    }
  }, [composerStorageKey]);
  useEffect(() => {
    const update = () => {
      composerMaxRef.current = Math.min(window.innerHeight * 0.6, 520);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const onComposerResizeDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const onWindowPointerMove = (event: PointerEvent) => {
      const drag = composerDragRef.current;
      if (!drag) return;
      const delta = drag.startY - event.clientY; // drag up = grow
      const next = Math.min(composerMaxRef.current, Math.max(COMPOSER_MIN, drag.startH + delta));
      composerHeightRef.current = next;
      setComposerHeight(next);
      event.preventDefault();
    };

    const onWindowPointerUp = (event: PointerEvent) => {
      if (!composerDragRef.current) return;
      composerDragRef.current = null;
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        window.localStorage.setItem(composerStorageKey, String(composerHeightRef.current));
      } catch {
        /* noop */
      }
      event.preventDefault();
    };

    composerDragRef.current = { startY: e.clientY, startH: composerHeight };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onWindowPointerMove, { passive: false });
    window.addEventListener("pointerup", onWindowPointerUp, { passive: false });
    window.addEventListener("pointercancel", onWindowPointerUp, { passive: false });
    e.preventDefault();
  }, [COMPOSER_MIN, composerHeight, composerStorageKey]);

  // Per-message chip pins derived from the editor's segments at submit
  // time. Attached to the request body so the edge fn merges them into the
  // Corpus scope for THIS turn only.
  const messagePinsRef = useRef<{
    documentIds: string[];
    folderIds: string[];
    buckets: string[];
  } | null>(null);
  const collectMessagePinsFromSegments = useCallback((segs: ChatSegment[]) => {
    const docs = new Set<string>();
    const folders = new Set<string>();
    const buckets = new Set<string>();
    for (const s of segs) {
      if (s.type !== "chip") continue;
      if (!s.id) continue;
      if (s.kind === "doc") docs.add(s.id);
      else if (s.kind === "folder") folders.add(s.id);
      else buckets.add(s.id);
    }
    if (docs.size + folders.size + buckets.size === 0) {
      messagePinsRef.current = null;
      return;
    }
    messagePinsRef.current = {
      documentIds: [...docs],
      folderIds: [...folders],
      buckets: [...buckets],
    };
  }, []);
  // Segments payload for the next request; consumed by transport fetch.
  const pendingRequestSegmentsRef = useRef<ChatSegment[] | null>(null);
  // Read the latest value inside the transport fetch without recreating it.
  const scopeRef = useRef<ThreadScope>({ documentIds: [], folderIds: [], buckets: [] });
  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);
  // Snapshot at submit time so toggling mid-stream doesn't surprise the user.
  const turnTargetRef = useRef<"chat" | "canvas">("chat");
  // Per-token streaming bookkeeping for canvas turns.
  const streamBufferRef = useRef<string>("");
  const streamRafRef = useRef<number | null>(null);

  // Load persisted scope whenever the active thread changes.
  useEffect(() => {
    if (!threadId) {
      setScopeState({ documentIds: [], folderIds: [], buckets: [] });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const s = await getThreadScope(threadId);
        if (!cancelled) setScopeState(s);
      } catch (err) {
        console.warn("ChatPanel: scope load failed", err);
        if (!cancelled) setScopeState({ documentIds: [], folderIds: [], buckets: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  // Wrapper that persists on change (fire-and-forget; toast on failure).
  const updateScope = useCallback(
    (next: ThreadScope) => {
      setScopeState(next);
      if (!threadId) return;
      void setThreadScope(threadId, next).catch((err) => {
        console.warn("ChatPanel: scope persist failed", err);
        toast.error("Couldn't save Corpus scope");
      });
    },
    [threadId],
  );


  // Keep the latest threadId in a ref so the transport can pick up the
  // server-created id on the second turn WITHOUT rebuilding useChat (which
  // would remount and lose the in-memory conversation from the first turn).
  const threadIdRef = useRef<string | null>(threadId);
  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  const transport = useMemo(() => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/composer-chat`;
    return new DefaultChatTransport({
      api: url,
      fetch: async (input, init) => {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = new Headers(init?.headers);
        if (session?.access_token) {
          headers.set("Authorization", `Bearer ${session.access_token}`);
        }
        headers.set("Content-Type", "application/json");
        let body = init?.body;
        if (typeof body === "string") {
          try {
            const parsed = JSON.parse(body);
            parsed.workspace_id = workspaceId;
            parsed.piece_id = pieceId;
            const tid = threadIdRef.current;
            if (tid) parsed.thread_id = tid;
            if (messagePinsRef.current) {
              parsed.message_pins = messagePinsRef.current;
              messagePinsRef.current = null;
            }
            if (pendingRequestSegmentsRef.current) {
              parsed.user_segments = pendingRequestSegmentsRef.current;
              pendingRequestSegmentsRef.current = null;
            }
            body = JSON.stringify(parsed);
          } catch {
            /* leave as-is */
          }
        }
        return fetch(input, { ...init, headers, body });
      },
    });
  }, [workspaceId, pieceId]);

  // Stable chat id for the lifetime of this mount, so useChat is never
  // reset just because `threadId` changes from null to a server-created id.
  const [chatId] = useState(
    () => `${pieceId}:${threadId ?? "new"}:${Math.random().toString(36).slice(2, 10)}`,
  );

  const { messages, sendMessage, status } = useChat({
    id: chatId,
    messages: initialMessages,
    transport,
    onError: (err) => {
      const e = classifyError(err);
      setLocalError(e);
      toast.error(e.kind === "credits" ? "Out of AI credits" : e.message);
      // If a canvas turn errored, unlock the canvas with whatever buffer we have.
      if (turnTargetRef.current === "canvas") {
        if (streamRafRef.current != null) {
          window.cancelAnimationFrame(streamRafRef.current);
          streamRafRef.current = null;
        }
        composerBus.emit("draft:stream", {
          phase: "end",
          markdown: streamBufferRef.current,
          aborted: true,
        });
        streamBufferRef.current = "";
        turnTargetRef.current = "chat";
      }
    },
    onFinish: ({ message }) => {
      // If we started without a threadId, the server just created one — surface it.
      if (!threadId && threadCreateNotifiedRef.current !== "pending") {
        threadCreateNotifiedRef.current = "pending";
        void (async () => {
          try {
            const { threads } = await composerChatApi.listThreads(workspaceId, pieceId);
            const newest = threads.find((t) => !t.archived_at) ?? null;
            if (newest) onThreadCreated(newest.id);
          } catch {
            /* ignore */
          }
        })();
      }
      // Write-to-canvas: finish the stream so the canvas subscriber can flush
      // and save the final doc into the new revision it created on `start`.
      if (turnTargetRef.current === "canvas" && message?.role === "assistant") {
        const md = (message.parts ?? [])
          .map((p) => (p.type === "text" ? p.text : ""))
          .join("");
        if (streamRafRef.current != null) {
          window.cancelAnimationFrame(streamRafRef.current);
          streamRafRef.current = null;
        }
        composerBus.emit("draft:stream", { phase: "end", markdown: md });
        streamBufferRef.current = "";
        turnTargetRef.current = "chat";
      }
      onTurnFinished();
      requestAnimationFrame(() => focusComposer());
    },
  });

  // Per-token chunk emitter for canvas turns. Watches the latest assistant
  // message text and forwards cumulative markdown to canvas subscribers,
  // throttled via rAF so we never re-render the editor more than once a frame.
  useEffect(() => {
    if (turnTargetRef.current !== "canvas") return;
    if (status !== "submitted" && status !== "streaming") return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    const text = messageText(last);
    if (text === streamBufferRef.current) return;
    streamBufferRef.current = text;
    if (streamRafRef.current != null) return;
    streamRafRef.current = window.requestAnimationFrame(() => {
      streamRafRef.current = null;
      composerBus.emit("draft:stream", {
        phase: "chunk",
        markdown: streamBufferRef.current,
      });
    });
  }, [messages, status]);

  useEffect(() => {
    requestAnimationFrame(() => focusComposer());
  }, [pieceId, threadId, focusComposer]);

  // Attach any pending segments to the newly-added user message so its
  // bubble renders chip pills in rendered history.
  useEffect(() => {
    const pending = pendingSegmentsRef.current;
    if (!pending) return;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "user") continue;
      if (segmentsById.has(m.id)) return;
      const next = new Map(segmentsById);
      next.set(m.id, pending);
      setSegmentsById(next);
      pendingSegmentsRef.current = null;
      return;
    }
  }, [messages, segmentsById]);

  const isLoading = status === "submitted" || status === "streaming";

  const submitFromEditor = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return;
    const text = editor.getPlainText().trim();
    if (!text) return;
    const segs = editor.getSegments();
    setLocalError(null);
    lastUserTextRef.current = text;
    turnTargetRef.current = writeToCanvas ? "canvas" : "chat";
    collectMessagePinsFromSegments(segs);
    pendingRequestSegmentsRef.current = segs;
    pendingSegmentsRef.current = segs;
    if (writeToCanvas) {
      streamBufferRef.current = "";
      composerBus.emit("draft:stream", {
        phase: "start",
        source: "chat",
        label: "Excerptus AI draft",
        mode: canvasMode as DraftStreamMode,
      });
    }
    editor.clear();
    await sendMessage({ text });
  }, [canvasMode, collectMessagePinsFromSegments, sendMessage, writeToCanvas]);

  // Auto-send invoked by ChatInsertBridge when a Vault prompt is pushed with
  // `autoSend: true`. Canvas-mode intentionally ignored — Prompts route to
  // AI Chat, not the canvas.
  const handlePromptAutoSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setLocalError(null);
      lastUserTextRef.current = trimmed;
      turnTargetRef.current = "chat";
      messagePinsRef.current = null;
      pendingRequestSegmentsRef.current = null;
      pendingSegmentsRef.current = null;
      editorRef.current?.clear();
      void sendMessage({ text: trimmed });
    },
    [sendMessage],
  );


  const handleRetry = useCallback(() => {
    const text = lastUserTextRef.current;
    if (!text) return;
    setLocalError(null);
    void sendMessage({ text });
  }, [sendMessage]);

  const handleSendToCanvas = (m: UIMessage) => {
    const full = messageText(m).trim();
    if (!full) return;
    const node = messageRefs.current.get(m.id) ?? null;
    const selected = getSelectionWithin(node);
    const text = selected ?? full;
    composerBus.emit("draft:insert", {
      doc: markdownToTiptapDoc(text),
      mode: "append-at-cursor",
      source: `chat:${m.id}`,
      label: selected ? "Excerptus AI selection" : "Excerptus AI reply",
    });
    toast.success(selected ? "Sent selection to Draft Canvas" : "Sent to Draft Canvas");
  };

  const isEmpty = messages.length === 0;

  return (
    <>
      <Conversation className="min-h-0 flex-1">
        <ConversationContent>
          {isEmpty ? (
            <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MessageSquare className="h-5 w-5" />
              </div>
              <p className="mt-3 text-sm font-medium text-foreground">
                Brainstorm, refine, restructure
              </p>
              <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                Ask Excerptus AI about your draft. Push a Prompt from the Vault to start, or pick one below.
              </p>
              <StarterChips
                onPick={(text) => {
                  editorRef.current?.setPlainText(text);
                  window.setTimeout(() => editorRef.current?.focus(), 0);
                }}
              />
            </div>
          ) : (
            messages.map((m) => {
              const text = messageText(m);
              const toolParts = m.parts.filter((p): p is ToolPart =>
                typeof p.type === "string" &&
                (p.type.startsWith("tool-") || p.type === "dynamic-tool"),
              );
              if (!text && toolParts.length === 0 && m.role !== "assistant") return null;
              const isUser = m.role === "user";
              return (
                <div
                  key={m.id}
                  className="group/msg relative"
                  ref={(el) => {
                    if (el) messageRefs.current.set(m.id, el);
                    else messageRefs.current.delete(m.id);
                  }}
                >
                  <Message from={isUser ? "user" : "assistant"}>
                    <MessageContent>
                      {isUser ? (
                        <UserMessageBody
                          segments={segmentsById.get(m.id) ?? null}
                          fallbackText={text}
                        />
                      ) : (
                        <>
                          {toolParts.map((p, idx) => {
                            const isDynamic = p.type === "dynamic-tool";
                            const out = (p as { output?: unknown }).output;
                            const img =
                              out && typeof out === "object" && "image_url" in out
                                ? (out as { image_url?: unknown }).image_url
                                : null;
                            const isErr = p.state === "output-error";
                            return (
                              <Tool key={`${m.id}-tool-${idx}`} defaultOpen={isErr}>
                                <ToolHeader
                                  {...(isDynamic
                                    ? { type: "dynamic-tool" as const, state: p.state, toolName: (p as { toolName: string }).toolName }
                                    : { type: p.type as `tool-${string}`, state: p.state })}
                                />
                                <ToolContent>
                                  <ToolInput input={p.input} />
                                  <ToolOutput
                                    output={
                                      typeof img === "string" ? (
                                        <div className="mt-2 space-y-1.5">
                                          <img
                                            src={img}
                                            alt={(p.input as { prompt?: string } | undefined)?.prompt ?? "Generated image"}
                                            className="max-h-64 rounded border border-border"
                                          />
                                          <div className="flex flex-wrap gap-1">
                                            <Button
                                              size="icon-sm"
                                              variant="ghost"
                                              title="Copy image URL"
                                              onClick={() => {
                                                void navigator.clipboard.writeText(img);
                                                toast.success("Image URL copied");
                                              }}
                                            >
                                              <Copy className="h-3.5 w-3.5" />
                                              <span className="sr-only">Copy URL</span>
                                            </Button>
                                            <Button
                                              size="icon-sm"
                                              variant="ghost"
                                              asChild
                                              title="Open in new tab"
                                            >
                                              <a href={img} target="_blank" rel="noreferrer">
                                                <ExternalLink className="h-3.5 w-3.5" />
                                                <span className="sr-only">Open</span>
                                              </a>
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <pre className="whitespace-pre-wrap break-words text-xs">
                                          {typeof out === "string" ? out : JSON.stringify(out, null, 2)}
                                        </pre>
                                      )
                                    }
                                    errorText={(p as { errorText?: string }).errorText}
                                  />
                                  {isErr && lastUserTextRef.current ? (
                                    <div className="flex justify-end pt-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleRetry}
                                        disabled={isLoading}
                                      >
                                        <RefreshCw className="mr-1 h-3.5 w-3.5" /> Retry
                                      </Button>
                                    </div>
                                  ) : null}
                                </ToolContent>
                              </Tool>
                            );
                          })}
                          {text ? <ChatMarkdown resolveChip={resolveChip}>{text}</ChatMarkdown> : null}
                        </>
                      )}
                    </MessageContent>
                    {!isUser && text ? (
                      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover/msg:opacity-100 [@media(hover:none)]:opacity-100">
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => handleSendToCanvas(m)}
                          disabled={isLoading}
                          title="Send to Draft Canvas (selection if any)"
                        >
                          <FileDown className="h-3.5 w-3.5" />
                          <span className="sr-only">Send to Draft Canvas</span>
                        </Button>
                      </div>
                    ) : null}
                  </Message>
                </div>
              );
            })
          )}
          {status === "submitted" ? (
            <Message from="assistant">
              <MessageContent>
                <Shimmer>
                  {turnTargetRef.current === "canvas" ? "Writing into draft…" : "Thinking…"}
                </Shimmer>
              </MessageContent>
            </Message>
          ) : null}
          {localError ? (
            <Message from="assistant">
              <MessageContent className="border border-destructive/40 bg-destructive/5 text-destructive">
                <p className="text-sm font-medium">
                  {localError.kind === "credits"
                    ? "Out of AI credits"
                    : localError.kind === "rate-limit"
                      ? "Rate limited"
                      : "Something went wrong"}
                </p>
                <p className="mt-1 text-xs opacity-90">{localError.message}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {localError.kind === "credits" ? (
                    <Button asChild size="sm" variant="outline">
                      <Link to="/settings?focus=ai-credits&buy_credits=1">Buy credits</Link>
                    </Button>
                  ) : null}
                  {lastUserTextRef.current ? (
                    <Button size="sm" variant="outline" onClick={handleRetry}>
                      <RefreshCw className="mr-1 h-3.5 w-3.5" /> Retry
                    </Button>
                  ) : null}
                </div>
              </MessageContent>
            </Message>
          ) : null}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      {readOnly ? (
        <div className="border-t border-border bg-muted/30 px-3 py-2 text-center text-xs text-muted-foreground">
          Read-only — this thread belongs to another workspace member.
        </div>
      ) : (
        <div ref={composerRef} className="relative border-t border-border p-2 pt-3 sm:p-3 sm:pt-4">
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize chat input"
            onPointerDown={onComposerResizeDown}
            className="absolute left-0 right-0 top-0 z-10 flex h-5 -translate-y-1/2 cursor-ns-resize select-none items-center justify-center"
            title="Drag to resize the chat input"
          >
            <span className="z-10 flex h-4 w-3 rotate-90 items-center justify-center rounded-sm border bg-border">
              <GripVertical className="h-2.5 w-2.5" />
            </span>

          </div>
          <div className="mb-1.5 flex items-center gap-2">
            <CorpusPickerPopover
              workspaceId={workspaceId}
              scope={scope}
              onChange={updateScope}
              disabled={isLoading}
            />
          </div>
          <ChatInsertBridge editorRef={editorRef} onAutoSend={handlePromptAutoSend} />
          <div className="relative">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isLoading) return;
                void submitFromEditor();
              }}
              className="flex flex-col gap-2"
            >
              <ChatComposerEditor
                ref={editorRef}
                placeholder={isEmpty ? "Ask Excerptus AI… (type / to scope a Corpus bucket, folder, or doc)" : "Ask Excerptus AI…"}
                disabled={isLoading}
                minHeight={88}
                heightPx={composerHeight}
                onSubmit={() => {
                  if (isLoading) return;
                  void submitFromEditor();
                }}
                onSlashTrigger={() => setSlashOpen(true)}
              />
              <div className="flex items-center justify-between gap-2">
                <div
                  className="flex items-center gap-1.5 text-[11px] font-normal text-muted-foreground"
                  title="Choose how the reply lands in the Draft Canvas."
                >
                  <span>Write to Canvas:</span>
                  <Select
                    value={canvasMode}
                    onValueChange={(v) => setCanvasMode(v as "off" | DraftStreamMode)}
                  >
                    <SelectTrigger className="h-6 w-[110px] px-2 py-0 text-[11px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off" className="text-xs">
                        Off — chat only
                      </SelectItem>
                      <SelectItem value="new" className="text-xs">
                        New draft
                      </SelectItem>
                      <SelectItem value="append" className="text-xs">
                        Append
                      </SelectItem>
                      <SelectItem value="replace" className="text-xs">
                        Replace
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <PromptInputSubmit status={status} disabled={isLoading}>
                  <ArrowUp />
                </PromptInputSubmit>
              </div>
            </form>
            {/* Programmatically-opened picker for the `/` shortcut. */}
            <div className="absolute left-2 top-2">
              <SlashInlinePicker
                workspaceId={workspaceId}
                scope={scope}
                open={slashOpen}
                onOpenChange={setSlashOpen}
                disabled={isLoading}
                editorRef={editorRef}
              />
            </div>
          </div>

        </div>


      )}
    </>
  );
};

// ---------- Outer wrapper that owns thread list + active id ----------

export const ChatPanel = ({ workspaceId, pieceId }: Props) => {
  const [threads, setThreads] = useState<ComposerChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  // Remount key for `ChatThreadView`. Stays constant across the
  // `null → server-created id` transition on the first turn so the
  // in-memory conversation isn't discarded. Only user-initiated switches
  // (switcher/hashchange/foreign-exit/piece-change) bump this.
  const [viewKey, setViewKey] = useState<string>("new");
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  // Metadata for a foreign (other-user) thread the workspace owner opened
  // via the inbox deep link. Drives the read-only banner + suppresses the
  // switcher's destructive controls.
  const [foreignThread, setForeignThread] = useState<{
    id: string;
    title: string | null;
    authorName: string | null;
  } | null>(null);

  const loadThreads = useCallback(async () => {
    if (!workspaceId) return [] as ComposerChatThread[];
    const attempt = async () => {
      const { threads: rows } = await composerChatApi.listThreads(workspaceId, pieceId);
      return rows;
    };
    try {
      const rows = await attempt();
      setThreads(rows);
      return rows;
    } catch (err) {
      // Cold-start transport blips (auth not yet hydrated) — retry once silently.
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("Failed to send a request")) {
        await new Promise((r) => setTimeout(r, 500));
        try {
          const rows = await attempt();
          setThreads(rows);
          return rows;
        } catch (retryErr) {
          console.debug("ChatPanel: list threads failed (after retry)", retryErr);
          return [] as ComposerChatThread[];
        }
      }
      console.warn("ChatPanel: list threads failed", err);
      return [] as ComposerChatThread[];
    }
  }, [workspaceId, pieceId]);

  // Initial load + active resolution from hash → most-recent → null.
  useEffect(() => {
    let cancelled = false;
    setThreadsLoaded(false);
    setForeignThread(null);
    void (async () => {
      const rows = await loadThreads();
      if (cancelled) return;
      const hashed = readChatHash();
      const live = rows.filter((t) => !t.archived_at);
      let next: string | null = null;
      if (hashed && rows.some((t) => t.id === hashed)) next = hashed;
      else if (live.length > 0) next = live[0].id;
      setActiveThreadId(next);
      setViewKey(next ?? "new");
      writeChatHash(next);
      setThreadsLoaded(true);

      // Hashed thread isn't in our own list — could be a foreign thread the
      // workspace owner is viewing via the inbox deep link.
      if (hashed && !rows.some((t) => t.id === hashed)) {
        try {
          const { thread } = await composerChatApi.getThread(workspaceId, hashed);
          if (cancelled) return;
          if (!thread.is_mine) {
            setForeignThread({
              id: thread.id,
              title: thread.title,
              authorName: thread.author_display_name,
            });
            setActiveThreadId(thread.id);
            setViewKey(thread.id);
            writeChatHash(thread.id);
          }
        } catch {
          /* not accessible — leave active as resolved above */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, pieceId, loadThreads]);

  // React to manual hash changes (back/forward, inbox deep links).
  useEffect(() => {
    const onHash = () => {
      const id = readChatHash();
      if (id && id !== activeThreadId) {
        setForeignThread(null);
        setActiveThreadId(id);
        setViewKey(id);
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [activeThreadId]);

  const setActive = useCallback((id: string) => {
    setForeignThread(null);
    setActiveThreadId(id);
    // Force a fresh mount even when the user reselects the already-active
    // thread. This makes the selector a reliable recovery path for any stale
    // or blank in-memory chat runtime.
    setViewKey(`${id}:${Date.now()}`);
    writeChatHash(id);
    void composerChatApi.markRead(workspaceId, id).catch(() => {});
  }, [workspaceId]);

  // Mark active thread read whenever it changes.
  useEffect(() => {
    if (!activeThreadId) return;
    void composerChatApi.markRead(workspaceId, activeThreadId).catch(() => {});
  }, [workspaceId, activeThreadId]);

  // Called when the first turn of a null-thread mount produced a server
  // thread. Adopt the id WITHOUT bumping viewKey so ChatThreadView keeps
  // its in-memory conversation.
  const handleThreadCreated = useCallback((threadId: string) => {
    setForeignThread(null);
    setActiveThreadId(threadId);
    writeChatHash(threadId);
    void composerChatApi.markRead(workspaceId, threadId).catch(() => {});
    void loadThreads();
  }, [loadThreads, workspaceId]);

  const handleTurnFinished = useCallback(() => {
    void loadThreads();
  }, [loadThreads]);

  const handleThreadDeleted = useCallback((deletedId: string) => {
    void loadThreads().then((rows) => {
      if (deletedId !== activeThreadId) return;
      const live = rows.filter((t) => !t.archived_at && t.id !== deletedId);
      const next = live[0]?.id ?? null;
      setActiveThreadId(next);
      setViewKey(next ?? `empty-${Math.random().toString(36).slice(2, 8)}`);
      writeChatHash(next);
    });
  }, [loadThreads, activeThreadId]);

  const exitForeignThread = useCallback(() => {
    setForeignThread(null);
    const live = threads.filter((t) => !t.archived_at);
    const next = live[0]?.id ?? null;
    setActiveThreadId(next);
    setViewKey(next ?? "new");
    writeChatHash(next);
  }, [threads]);

  if (!threadsLoaded) {
    return (
      <div className="flex h-full items-center justify-center rounded-md border border-border bg-background text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  const isReadOnly = Boolean(foreignThread && foreignThread.id === activeThreadId);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-md border border-border bg-background">
      <div className="flex items-center justify-between gap-2 border-b border-border px-2 py-1.5">
        {isReadOnly && foreignThread ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={exitForeignThread}
              title="Back to my threads"
            >
              ← My threads
            </Button>
            <span className="truncate text-xs text-muted-foreground">
              {foreignThread.title || "Untitled thread"} ·{" "}
              <span className="text-foreground">
                {foreignThread.authorName || "Collaborator"}
              </span>
            </span>
          </div>
        ) : (
          <ChatThreadSwitcher
            workspaceId={workspaceId}
            pieceId={pieceId}
            threads={threads}
            activeThreadId={activeThreadId}
            onSelect={setActive}
            onChanged={() => void loadThreads()}
            onDeleted={handleThreadDeleted}
          />
        )}
        <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
          <Link to="/composer/inbox" title="Chat inbox across all Pieces">
            Inbox
          </Link>
        </Button>
      </div>

      <ChatThreadView
        key={`${pieceId}:${viewKey}`}
        workspaceId={workspaceId}
        pieceId={pieceId}
        threadId={activeThreadId}
        onThreadCreated={handleThreadCreated}
        onTurnFinished={handleTurnFinished}
        readOnly={isReadOnly}
      />
    </div>
  );
};


export default ChatPanel;
